import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { message } from 'antd';
import pushNotificationService from '@/services/pushNotification.service';
import api from '@/services/api.service';
import {
  drainOfflineNotificationClicks,
  queueOfflineNotificationClick,
} from '@/utils/offlineNotificationQueue';

/**
 * Handles service worker postMessage from push notification clicks with permission-safe navigation.
 * Also handles snooze actions and offline click queue.
 */
export function PushNotificationListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const [offlineHint, setOfflineHint] = useState<string | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK_OFFLINE') {
        const url = typeof event.data.url === 'string' ? event.data.url : '/home/approvals';
        const doc = event.data.document_number || 'approval';
        void queueOfflineNotificationClick({
          url,
          notification_id: event.data.notification_id,
          document_number: event.data.document_number,
        }).then(() => {
          setOfflineHint(`Network unavailable — will open ${doc} when connected.`);
          message.warning(`Network unavailable — will open ${doc} when connected.`);
        });
        return;
      }

      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const snoozeMinutes = event.data.snooze_minutes
          ? Number(event.data.snooze_minutes)
          : null;
        const notificationId =
          typeof event.data.notification_id === 'string' ? event.data.notification_id : null;

        if (snoozeMinutes && notificationId) {
          void api
            .post(`/notifications/${notificationId}/snooze`, { minutes: snoozeMinutes })
            .then(() => message.success(`Snoozed for ${snoozeMinutes} minutes`))
            .catch(() => message.error('Could not snooze notification'));
          return;
        }

        const rawUrl = typeof event.data.url === 'string' ? event.data.url : '/home/approvals';
        void pushNotificationService.openNotificationUrl(rawUrl, notificationId).then((url) => {
          navigate(url);
        });
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const snooze = params.get('snooze');
    const notificationId = params.get('notification_id');
    if (snooze && notificationId) {
      const minutes = Number(snooze);
      void api
        .post(`/notifications/${notificationId}/snooze`, { minutes })
        .then(() => message.success(`Snoozed for ${minutes} minutes`))
        .catch(() => message.error('Could not snooze notification'))
        .finally(() => {
          navigate('/home/approvals', { replace: true });
        });
      return;
    }
    const fromPush = params.get('from_push');
    if (!fromPush) return;
    void pushNotificationService.openNotificationUrl(fromPush, notificationId).then((url) => {
      navigate(url, { replace: true });
    });
  }, [location.search, navigate]);

  useEffect(() => {
    const flush = () => {
      if (!navigator.onLine) return;
      void drainOfflineNotificationClicks().then((items) => {
        if (!items.length) return;
        const first = items[0];
        setOfflineHint(null);
        void pushNotificationService
          .openNotificationUrl(first.url, first.notification_id)
          .then((url) => navigate(url));
      });
    };
    window.addEventListener('online', flush);
    flush();
    return () => window.removeEventListener('online', flush);
  }, [navigate]);

  if (offlineHint) {
    // Hint is also shown via antd message; keep null UI
    return null;
  }
  return null;
}
